import { db } from "../firebase/firebase";
import { doc, getDoc } from "firebase/firestore";

/* ----------------------------------------------
   Helpers
---------------------------------------------- */
const nz = (v) => (v == null || v === "" ? 0 : Number(v));
const norm = (s = "") => (s || "").toString().trim();
const toClientId = (s = "") => norm(s).toLowerCase().replace(/ /g, "_");

/* ----------------------------------------------
   Distance (Haversine)
---------------------------------------------- */
function haversineKm(lat1, lon1, lat2, lon2) {
  if (
    !lat1 || !lon1 || !lat2 || !lon2 ||
    isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)
  ) return null;

  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
/* ----------------------------------------------
   KOI Pricing (20 KM Boundary Method)
---------------------------------------------- */
function priceKOI({
  pickup,
  drop,
  pickupLat,
  pickupLng,
  dropLat,
  dropLng,
  carType,
  cfg
}) {
  // 🚀 New Rule: Detect executive car requests inside comments
// ⭐ NEW RULE: Detect executive requests in comment
const commentText = (cfg?.currentRowComment || "").toLowerCase();
const commentExec =
  /lexus|tesla|model|executive|premium|es ?300|s[- ]?class/.test(commentText);

// If comment says executive → force executive
const t = commentExec
  ? "executive"
  : (carType || "").toLowerCase().includes("exec")
  ? "executive"
  : "standard";



  const s = cfg?.rules?.koiRates || {};

  const p = (pickup || "").toLowerCase();
  const d = (drop || "").toLowerCase();

  // ⭐ STRONG AIRPORT DETECTION
  const airportRegex =
    /(dxb|dubai intl|dubai international|airport|air port|terminal|t ?1|t ?2|t ?3)/i;

  const isAirport = airportRegex.test(p) || airportRegex.test(d);

  // Abu Dhabi flat rate
  const isAbuDhabi =
    /(abu dhabi|auh)/i.test(p) || /(abu dhabi|auh)/i.test(d);

  if (isAbuDhabi) {
    return nz(s.abuDhabi?.[t]);
  }

  // ⭐ AIRPORT LOGIC (20 KM boundary)
  if (isAirport) {
    const DXB = { lat: 25.2532, lng: 55.3657 };
    let distanceKm = null;

    const pickupIsAirport = airportRegex.test(p);
    const dropIsAirport = airportRegex.test(d);

    if (pickupIsAirport && dropLat && dropLng) {
      distanceKm = haversineKm(DXB.lat, DXB.lng, dropLat, dropLng);
    } else if (dropIsAirport && pickupLat && pickupLng) {
      distanceKm = haversineKm(DXB.lat, DXB.lng, pickupLat, pickupLng);
    } else {
      return 0; // unknown → no coordinates
    }

    const threshold = cfg?.rules?.distanceThresholdKm ?? 20;
    const inner = distanceKm <= threshold;

    return inner
      ? nz(s.airportWithinBoundary?.[t])
      : nz(s.airportBeyondBoundary?.[t]);
  }

  return 0;
}


/* ----------------------------------------------
   City Guide Rule
---------------------------------------------- */
function driverPayCityGuide(clientFinal, driverParking) {
  const allowance = 5;
  const extra = Math.max(nz(driverParking) - allowance, 0);
  return Math.max(nz(clientFinal) - extra, 0);
}

/* ----------------------------------------------
   Totals
---------------------------------------------- */
export function computeTotals({
  clientFinalPrice = 0,
  clientParking = 0,
  driverFinalPrice = 0,
  driverParking = 0,
}) {
  const clientTotal = nz(clientFinalPrice) + nz(clientParking);
  const driverTotal = nz(driverFinalPrice) + nz(driverParking);
  return { clientTotal, driverTotal, profit: clientTotal - driverTotal };
}

/* ----------------------------------------------
   Normalize Row (Do not modify)
---------------------------------------------- */
export function mapCsvRow(csvRow) {
  return {
    reservationId: norm(csvRow.reservationId),
    status: norm(csvRow.status),

    pickupDate: norm(csvRow.pickupDate),
    pickupTime: norm(csvRow.pickupTime),
    flightNumber: norm(csvRow.flightNumber),

    numberOfPassengers: norm(csvRow.numberOfPassengers),
    passengerName: norm(csvRow.passengerName),
    customerPhone: norm(csvRow.customerPhone),
    driver: norm(csvRow.driver),

    pickup: norm(
      csvRow.pickupAddress ||
        csvRow.pickup_location ||
        csvRow.pickupLocation ||
        csvRow.pickup ||
        ""
    ),

    drop: norm(
      csvRow.dropoffAddress ||
        csvRow.drop_location ||
        csvRow.dropLocation ||
        csvRow.drop ||
        ""
    ),

    pickupLat: csvRow.pickupLat ? Number(csvRow.pickupLat) : null,
    pickupLng: csvRow.pickupLng ? Number(csvRow.pickupLng) : null,
    dropLat: csvRow.dropLat ? Number(csvRow.dropLat) : null,
    dropLng: csvRow.dropLng ? Number(csvRow.dropLng) : null,

    carType: norm(csvRow.carType),
    comment: norm(csvRow.comment),
    clientName: norm(csvRow.Client),

    clientFinalPrice: nz(csvRow.clientFinalPrice),
    driverFinalPrice: nz(csvRow.driverFinalPrice),
    clientParking: nz(csvRow.clientParking),
    driverParking: nz(csvRow.driverParking),
  };
}

/* ----------------------------------------------
   Main Auto-Pricing Logic
---------------------------------------------- */
export async function autoPriceRow(csvRow) {
  const row = mapCsvRow(csvRow);
  const clientId = toClientId(row.clientName);

  if (!clientId) {
    return {
      ...row,
      statusNote: "Missing client",
      needsManual: true,
      ...computeTotals(row),
    };
  }

  const snap = await getDoc(doc(db, "clients", clientId));
  if (!snap.exists()) {
    return {
      ...row,
      statusNote: "Client not configured",
      needsManual: true,
      ...computeTotals(row),
    };
  }

  const cfg = snap.data();

  let suggestedClient = 0;
  let suggestedDriver = 0;

  // KOI pricing
  if (cfg.pricingMode === "koi") {
    suggestedClient = priceKOI({
      pickup: row.pickup,
      drop: row.drop,
      pickupLat: row.pickupLat,
      pickupLng: row.pickupLng,
      dropLat: row.dropLat,
      dropLng: row.dropLng,
      carType: row.carType,
      cfg,
    });

    suggestedDriver =
      suggestedClient > 0
        ? Math.max(suggestedClient - (cfg?.driverPay?.minus8Value ?? 8), 0)
        : 0;
  }

  // City Guide mode
  else if (cfg.pricingMode === "cityGuide") {
    suggestedClient = 0;
    suggestedDriver = driverPayCityGuide(
      suggestedClient,
      row.driverParking
    );
  }

  // Manual mode
  else {
    suggestedClient = 0;
    suggestedDriver = 0;
  }

  const clientFinalPrice = nz(row.clientFinalPrice) || suggestedClient;

  const driverFinalPrice =
    cfg.pricingMode === "cityGuide"
      ? driverPayCityGuide(clientFinalPrice, row.driverParking)
      : nz(row.driverFinalPrice) || suggestedDriver;

  const priced = {
    ...row,
    clientId,
    clientSuggestedPrice: suggestedClient,
    driverSuggestedPrice: suggestedDriver,
    clientFinalPrice,
    driverFinalPrice,
  };

  const totals = computeTotals(priced);

  return {
    ...priced,
    ...totals,
    needsManual: clientFinalPrice === 0,
    statusNote:
      clientFinalPrice === 0 ? "Pending pricing" : "Auto-priced",
  };
}

export { priceKOI };
